/* @flow weak */

'use strict';

const React = require('react');
const Reflux = require('reflux');
const _ = require('lodash');

const AnswerStore = require('../stores/AnswerStore.jsx');
const AnswerActions = require('../actions/AnswerActions.jsx');
const PartStore = require('../stores/PartStore.jsx');
const ProductStore = require('../stores/ProductStore.jsx');
const QuestionStore = require('../stores/QuestionStore.jsx');
const SKUStore = require('../stores/SKUStore.jsx');
const ValidatorStore = require('../stores/ValidatorStore.jsx');

const Config = require('../configs/Config.js');
const Loader = require('../loaders');
const ln = Loader.ln || 'en_US';

const ControlMixin = {
  mixins: [],

  propTypes: {
    question: React.PropTypes.object.isRequired,
    parts: React.PropTypes.array.isRequired
  },

  productName: '',
  questionGroup: '',
  questionName: '',
  priceType: '',
  currency: '',
  scrollOnAnswer: false,

  getInitialState() {
    return {
      answer: undefined, // part id
      textInput: undefined, // user inputted text
      changed: false,
      locked: false
    };
  },

  componentWillMount() {
    const { question } = this.props;
    var country = !_.isNull(ProductStore.getSchoolCountry()) && !_.isEmpty(ProductStore.getSchoolCountry()) 
      && ProductStore.getSchoolCountry() || 'default';
    this.currency = _.get(Config, 'currency.' + country) || '$';
    this.productName = ProductStore.getProductName();
    this.questionGroup = _.get(question, `metadata.lang.${ln}.QuestionGroup`);
    this.questionName = _.get(question, `metadata.lang.${ln}.DisplayName`) || question.name;

    if (!!_.get(this.props.question, 'metadata.ScrollOnAnswer')) {
      this.scrollOnAnswer = true;
    }

    // Set the price type. Base if the question is in the SKU Map. Otherwise it's an upcharge
    const skuMap = ProductStore.getSKUMap();
    const questionIds = _.map(_.get(skuMap, [0, 'pairs']), 'q');
    this.priceType = _.includes(questionIds, this.props.question.id) ? 'base' : 'upcharge';

    // Fetch all saved configuration answers
    let answers = AnswerStore.fetchAll();

    // If a default part exists and there is no saved configuration answer for the associated question, inject it as an answer
    _.each(this.props.parts, part => {
      if (part.default === '1') {
        if (!answers[question.id]) {
          answers[question.id] = { input: null, part, question };
          AnswerActions.update(question, part, undefined, false);
        }
      }
    });

    // Hydrate the control with pre-selected answers and/or default parts if they exist.
    this.hydrateAnswers(answers);

    // And listen to future changes (slow async load of previous configuration not caught above)
    this.unsubscribe = AnswerStore.listen(this.hydrateAnswers);
  },

  componentWillUnmount() {
    this.unsubscribe();
  },

  hydrateAnswers(answers) {
    let newState = {};

    let sku = AnswerStore.getSKU();
    if (this.state.sku === undefined || sku !== this.state.sku) {
      newState.sku = sku;
    }

    const matchingAnswer = answers[this.props.question.id];

    if (matchingAnswer !== undefined) {
      newState.locked = matchingAnswer.locked;

      if (matchingAnswer.part != null && matchingAnswer.part.id !== this.state.answer) {
        newState.answer = matchingAnswer.part.id;
        newState.changed = true;
      }
      if (matchingAnswer.input !== this.state.textInput) {
        newState.textInput = matchingAnswer.input;
        newState.changed = true;
      }

      if (typeof this.handleTextHydration === 'function' && _.get(matchingAnswer, 'input')) {
        this.handleTextHydration(matchingAnswer.input);
      }
    } else {
      if (this.state.answer !== undefined) {
        newState.answer = undefined;
        newState.changed = true;
      }
      if (this.state.textInput !== undefined) {
        newState.textInput = undefined;
        newState.changed = true;
      }
    }

    if (!_.isEmpty(newState)) {
      this.setState(newState);
    }
  },

  trackUpdate(value, doNotTrack = false) {
    const trackingTarget = _.get(window, Config.analytics.eventTrackingTarget);
    const productData = ProductStore.getData();

    if (trackingTarget && typeof trackingTarget === 'function') {
      const event = 'uc_answer';
      const category = _.get(productData, 'metadata.GACategory') || _.get(Config, 'analytics.eventCategory');
      const action = this.questionGroup;
      const label = !doNotTrack ? value : '';

      trackingTarget(event, category, action, label);
    }
  },

  handleChange(e) {
    if (this.state.locked) {
      return;
    }

    if (e.target.value !== '') {
      let part = _.find(this.props.parts, { id: e.target.value });

      if (Config.analytics.enableTracking) {
        const partName = _.get(part, `metadata.lang.${ln}.DisplayName`) || part.name;
        this.trackUpdate(partName);
      }

      this.setState({
        answer: part.id,
        changed: true
      });

      AnswerActions.update(this.props.question, part);
    } else {
      if (Config.analytics.enableTracking) {
        this.trackUpdate('');
      }

      this.setState({
        answer: undefined,
        changed: true
      });

      AnswerActions.delete(this.props.question);
    }

    if (this.scrollOnAnswer) this.registerScrollCallback();
  },

  handleChangeText(inputValue) {
    if (this.state.locked) {
      return;
    }
    
    if (_.get(this.props.question, 'constraints.ForceUppercase')) {
      inputValue = inputValue.toUpperCase();
    }

    if (this.props.parts.length > 1) {
      // @TODO Convert this to a throw
      /*eslint-disable no-console */
      console.log('WARNING! This question with an associated text entry control has more than 1 part! Investigate!',
        this.props.question,
        this.props.parts);
      /*eslint-enable no-console */
    }

    let part = this.props.parts[0];

    this.setState({
      answer: _.get(part, 'id'),
      textInput: inputValue,
      changed: true
    });

    if (!inputValue) {
      AnswerActions.delete(this.props.question);
    } else {
      AnswerActions.update(this.props.question, part, inputValue, undefined, true);
    }
  },

  handleBlur(e) {
    if (Config.analytics.enableTracking) {
      this.trackUpdate(e.target.value, true);
    }
  },

  handleImageError(e) {
    let s3url = Config.images.s3url;
    let s3placeholderurl = s3url + 'placeholder.gif';

    e.target.src = s3placeholderurl;
  },

  getSkuPriceMain(part) {
    const partSkuPrices = AnswerStore.getSkuPricesForPart(part)
      .sort((a, b) => _.get(a, ['price', 'retail']) - _.get(b, ['price', 'retail']));

    if (partSkuPrices && partSkuPrices.length > 0) {
      return {
        retail_price: _.get(partSkuPrices, [0, 'retail']),
        full_price: _.get(partSkuPrices, [0, 'retail_full']),
        type: partSkuPrices.length > 1 ? 'startingAt' : 'simple'
      };
    }

    return;
  },

  getSkuPriceUpcharge(part) {
    let sku = _.get(part, 'metadata.SKU');
    let skus = SKUStore.getSKUPricing();
    let price = _.get(skus, sku);
    if (!sku || !price) {
      return;
    }

    return {
      retail_price: _.get(price, 'retail'),
      full_price: _.get(price, 'retail_full'),
      type: 'simple',
      is_upcharge: ProductStore.getProductLineId() == Config.productline.KSUNIVCollections
    };
  },

  getPrice(part) {
    var forcePriceHiding = _.get(this.props.question, 'metadata.ForcePriceHiding');
    if (forcePriceHiding == true ||forcePriceHiding == "true") {
      return;
    }

    if (Config.skuPricing) {
      if (this.priceType === 'upcharge' /*&& ProductStore.getProductLineId() != Config.productline.KSUNIVCollections*/) {
        return this.getSkuPriceUpcharge(part);
      } 
      /*else if (this.priceType === 'upcharge' && ProductStore.getProductLineId() == Config.productline.KSUNIVCollections) {
        return;
      }*/

      return this.getSkuPriceMain(part);
    }

    let productItemCode = ProductStore.getItemCode();
    let questionName = this.props.question.name;

    // The current question is the question responsible for choosing an ItemCode. Display "Starting at" price data.
    if (typeof productItemCode === 'object' && productItemCode[questionName]) {
      return _.assign({}, this.displayStartingAtPrice(part, productItemCode[questionName]), { type: 'startingAt' });
    }

    // If null or undefined, return nothing... and you'll like it.
    if (!part.pricing) {
      return undefined;
    }

    // If it's a string, return the value.
    if (_.values(part.pricing).length === 1) {
      let price = _.values(part.pricing)[0];
      return _.assign({}, price, { type: 'simple' });
    }

    // If we've gotten this far, pricing is an object with keys relating to item codes and their value being the price
    // Determine what the itemCode would be for the part currently being rendered
    let itemCode = AnswerStore.getItemCode(this.props.question.id, part.id);

    // itemCode is undefined. Display lowest price in object
    if (!itemCode) {
      let lowestPrice = _.sortBy(part.pricing, (x) => {
        return parseFloat(x.retail_price);
      })[0];

      return _.assign({}, lowestPrice, { type: 'startingAt' });
    } else {
      let price = _.get(part.pricing, [itemCode]);
      if (!price) {
        throw new Error('Error in price for part: ' + part.name + ' for itemcode ' + itemCode);
      }

      return _.assign({}, price, { type: 'simple' });
    }
  },

  displayPrice(part) {
    const price = this.getPrice(part);

    switch (_.get(price, 'type')) {
      case 'simple':
        return (price.is_upcharge ? ('+' + this.currency) : this.currency) + price.retail_price;
      case 'startingAt':
        return (
          <span><span className="starting-at-prefix">{Config.features.startingAtText[ln] || Config.features.startingAtText['en_US']}</span>{this.currency}{price.retail_price}</span>
          );
      default:
        return null;
    }
  },

  displayStartingAtPrice(part, itemCodes) {
    let itemCodeKey = _.get(part, 'metadata.ItemCodeKey');
    if (itemCodeKey === undefined) {
      return undefined;
    }

    let itemCode = _.get(itemCodes, [itemCodeKey]);
    if (itemCode === undefined) {
      return undefined;
    }

    let price = PartStore.fetchLowestPriceForItemCode(itemCode);

    return price;
  },

  getCurrency() {
    return this.currency;
  },

  formatPartName(partName) {
    if (_.get(this.props.question, 'metadata.RegExKey')) {
      // replace last space with line break
      return partName.replace(/ ([^ ]*)$/, '\n$1');
    }
    return partName;
  },

  displayPartTitle(part) {
    return this.formatPartName(_.get(part, `metadata.lang.${ln}.DisplayTitle`) || ' ');
  },

  displayPartName(part) {
    return this.formatPartName(_.get(part, `metadata.lang.${ln}.DisplayName`) || part.name);
  },

  isPartNamesHidden(question) {
    return _.get(question, 'metadata.hideOptionNames') || false;
  },

  getPartDetails(part) {
    return _.get(part, `metadata.lang.${ln}.DisplayDetails`);
  },

  registerScrollCallback() {
    this.scrollUnsubscribe = ValidatorStore.listen(this.scrollCallback);
  },

  scrollCallback() {
    if (_.isFunction(this.scrollUnsubscribe)) this.scrollUnsubscribe();
    this.handleScroll();
  },

  handleScroll() {
    let childQuestions = QuestionStore.getChildQuestions(this.props.question.id);
    let validationData = ValidatorStore.getValidationData();
    let hasVisibleChildQuestions = _.some(childQuestions, (question) => {
      return _.get(validationData.questions[question.id], 'visible');
    });

    if (hasVisibleChildQuestions) {
      // If yes, scroll to the bottom of the current question.
      return this.scrollToQuestion(this.props.question.name, 'bottom');
    }

    // Else scroll to the top of the targeted question (defined via metadata)
    AnswerActions.delete(QuestionStore.getQuestionByName('CharmSelectionControlGrid'));
    return this.scrollToQuestion('CharmSelectionControlGrid', 'top');
  },

  scrollToQuestion(questionName, position = 'top') {
    let className = '.uc-q-' + questionName;
    let element = document.querySelector(className);
    let elementScrollTop = element.getBoundingClientRect()[position];
    let fixedHeaderHeightTarget = _.get(Config, 'breakpoints.fixedHeaderHeightTarget');
    let fixedHeaderHeight = fixedHeaderHeightTarget ? _.get(document.querySelector(fixedHeaderHeightTarget), 'offsetHeight') || 0 : 0;

    window.scroll(0, elementScrollTop + window.scrollY - fixedHeaderHeight - 10);
  }
};

module.exports = ControlMixin;


// WEBPACK FOOTER //
// ./src/base/scripts/mixins/ControlMixin.jsx